import { useState, useEffect, useRef } from 'react';
import { Hand, MousePointer, Plus, BookOpen, Brain, FileText, Share2, Pencil, Palette, Ban, Trash2, Lightbulb } from 'lucide-react';
import { useT } from '../i18n';
import { useSettingsStore } from '../stores/settingsStore';

const BTN_ICON = "w-11 h-11 flex items-center justify-center rounded-[16px] transition-all hover:scale-105 active:scale-95";
const BTN_TEXT = "h-11 px-5 flex items-center justify-center rounded-[16px] transition-all font-semibold text-[14px] hover:scale-105 active:scale-95";

const formatDate = (iso, lang = 'en') => {
  const locale = lang === 'it' ? 'it-IT' : 'en-US';
  const d = new Date(iso);
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short' })
    + ' · ' + d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
};

const ReportDropdown = ({ reports, onGenerateReport, onOpenReport, isAnalyzing, hasNodes, onClose }) => {
  const ref = useRef(null);
  const t = useT();
  const lang = useSettingsStore((s) => s.lang);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute bottom-[calc(100%+10px)] right-0 w-72 rounded-[18px] overflow-hidden z-50"
      style={{ background: 'color-mix(in srgb, var(--bg-dim) 95%, transparent)', border: '1px solid var(--border)', backdropFilter: 'blur(16px)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}
    >
      <button
        onClick={() => { if (!isAnalyzing && hasNodes) { onGenerateReport(); onClose(); } }}
        disabled={isAnalyzing || !hasNodes}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${isAnalyzing || !hasNodes ? 'opacity-40 cursor-not-allowed' : ''}`}
        style={{ borderBottom: '1px solid var(--border-s)' }}
        onMouseEnter={e => { if (!isAnalyzing && hasNodes) e.currentTarget.style.background = 'var(--surface)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        {isAnalyzing ? (
          <span className="w-4 h-4 rounded-full border-2 animate-spin shrink-0" style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent)' }} />
        ) : (
          <span className="w-4 h-4 rounded-full border-2 border-dashed shrink-0" style={{ borderColor: 'var(--accent)' }} />
        )}
        <span className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>
          {isAnalyzing ? t('toolbar.analyzing') : hasNodes ? t('toolbar.generateReport') : t('toolbar.addNodeFirst')}
        </span>
      </button>

      {reports.length > 0 ? (
        <div className="max-h-56 overflow-y-auto">
          {reports.map((entry) => (
            <button
              key={entry.id}
              onClick={() => { onOpenReport(entry); onClose(); }}
              className="w-full flex items-start gap-3 px-4 py-3 text-left transition-colors"
              style={{ borderBottom: '1px solid var(--border-s)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <FileText size={14} className="shrink-0 mt-0.5" style={{ color: 'var(--accent-2)' }} />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium leading-snug line-clamp-1" style={{ color: 'var(--text-primary)' }}>
                  {entry.data.synthesis?.slice(0, 60)}…
                </p>
                <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-dim)' }}>{formatDate(entry.createdAt, lang)}</p>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <p className="px-4 py-3 text-[12px] italic" style={{ color: 'var(--text-dim)' }}>{t('toolbar.noReports')}</p>
      )}
    </div>
  );
};

const ToolbarDefault = ({
  isPanMode,
  onTogglePanMode,
  onAddNode,
  activeParentNode,
  onAnalyzeMap,
  hasNodes,
  isAnalyzing,
  reports,
  onOpenReport,
  flashcardMode,
  onToggleFlashcardMode,
  flashcardStats,
  onShareMap,
}) => {
  const [reportOpen, setReportOpen] = useState(false);
  const t = useT();

  return (
    <>
      <button
        onClick={onTogglePanMode}
        title={isPanMode ? t('toolbar.selectionMode') : t('toolbar.panMode')}
        className={BTN_ICON}
        style={{
          background: isPanMode ? 'var(--accent)' : 'var(--surface)',
          border: `1px solid ${isPanMode ? 'var(--accent)' : 'var(--border)'}`,
          color: isPanMode ? 'var(--bg-deeper)' : 'var(--text-muted)',
          boxShadow: isPanMode ? '0 0 12px color-mix(in srgb, var(--accent) 30%, transparent)' : 'none',
        }}
      >
        {isPanMode ? <MousePointer size={18} /> : <Hand size={18} />}
      </button>

      <button
        onClick={onToggleFlashcardMode}
        title={flashcardMode ? t('toolbar.exitFlashcard') : t('toolbar.flashcardMode')}
        className={BTN_ICON}
        style={{
          background: flashcardMode ? 'var(--warning)' : 'var(--surface)',
          border: `1px solid ${flashcardMode ? 'var(--warning)' : 'var(--border)'}`,
          color: flashcardMode ? 'var(--bg-deeper)' : 'var(--text-muted)',
          boxShadow: flashcardMode ? '0 0 12px color-mix(in srgb, var(--warning) 30%, transparent)' : 'none',
        }}
      >
        <BookOpen size={18} />
      </button>

      {flashcardStats?.total > 0 && (
        <div
          className="flex items-center gap-2 px-4 h-11 rounded-[16px] text-[13px] font-semibold"
          style={{
            background: flashcardMode ? 'color-mix(in srgb, var(--warning) 15%, transparent)' : 'var(--surface)',
            border: `1px solid ${flashcardMode ? 'var(--warning)' : 'var(--border)'}`,
            color: flashcardMode ? 'var(--warning)' : 'var(--text-muted)',
          }}
        >
          <span>{t('toolbar.studyDeck', { count: flashcardStats.total })}</span>
          <span className="text-[11px] opacity-80">{t('toolbar.studyReview', { count: flashcardStats.review })}</span>
          <span className="text-[11px] opacity-80">{t('toolbar.studyKnown', { count: flashcardStats.known })}</span>
        </div>
      )}

      <button
        onClick={onAddNode}
        disabled={!activeParentNode}
        title={activeParentNode ? t('toolbar.forkOf', { title: activeParentNode.title }) : t('toolbar.clickNodeFirst')}
        className={BTN_ICON}
        style={{
          background: activeParentNode ? 'color-mix(in srgb, var(--success) 20%, transparent)' : 'var(--surface)',
          border: `1px solid ${activeParentNode ? 'color-mix(in srgb, var(--success) 40%, transparent)' : 'var(--border)'}`,
          color: activeParentNode ? 'var(--success)' : 'var(--text-dim)',
          opacity: activeParentNode ? 1 : 0.4,
          cursor: activeParentNode ? 'pointer' : 'not-allowed',
        }}
      >
        <Plus size={18} />
      </button>

      <button
        onClick={onShareMap}
        disabled={!hasNodes}
        title={hasNodes ? t('toolbar.shareMap') : t('toolbar.addNodeFirst')}
        className={BTN_ICON}
        style={{
          background: hasNodes ? 'color-mix(in srgb, var(--accent-2) 15%, transparent)' : 'var(--surface)',
          border: `1px solid ${hasNodes ? 'color-mix(in srgb, var(--accent-2) 30%, transparent)' : 'var(--border)'}`,
          color: hasNodes ? 'var(--accent-2)' : 'var(--text-dim)',
          opacity: hasNodes ? 1 : 0.4,
          cursor: hasNodes ? 'pointer' : 'not-allowed',
        }}
      >
        <Share2 size={18} />
      </button>

      {/* Brain / Report button con dropdown */}
      <div className="relative">
        <button
          onClick={() => setReportOpen(prev => !prev)}
          title={t('toolbar.report')}
          className={BTN_ICON}
          style={{
            background: reportOpen ? 'color-mix(in srgb, var(--accent) 25%, transparent)' : 'color-mix(in srgb, var(--accent) 12%, transparent)',
            border: `1px solid color-mix(in srgb, var(--accent) ${reportOpen ? '50' : '25'}%, transparent)`,
            color: 'var(--accent)',
            boxShadow: reportOpen ? '0 0 12px color-mix(in srgb, var(--accent) 25%, transparent)' : 'none',
          }}
        >
          {isAnalyzing ? (
            <span className="w-[18px] h-[18px] rounded-full border-2 animate-spin" style={{ borderColor: 'var(--ctp-surface2)', borderTopColor: 'var(--accent)' }} />
          ) : (
            <div className="relative">
              <Brain size={18} />
              {reports.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 text-[8px] font-bold rounded-full flex items-center justify-center leading-none" style={{ background: 'var(--accent)', color: 'var(--bg-deeper)' }}>
                  {reports.length > 9 ? '9+' : reports.length}
                </span>
              )}
            </div>
          )}
        </button>

        {reportOpen && (
          <ReportDropdown
            reports={reports}
            onGenerateReport={onAnalyzeMap}
            onOpenReport={onOpenReport}
            isAnalyzing={isAnalyzing}
            hasNodes={hasNodes}
            onClose={() => setReportOpen(false)}
          />
        )}
      </div>
    </>
  );
};

const Divider = () => (
  <div className="w-px h-6 mx-1 self-center" style={{ background: 'var(--border)' }} />
);

const MobileBtn = ({ icon, onClick, active, danger }) => {
  const Icon = icon;
  return (
    <button
      onClick={onClick}
      className="w-12 h-12 flex items-center justify-center rounded-2xl transition-all active:scale-95"
      style={{
        color: danger ? 'var(--danger)' : active ? 'var(--accent)' : 'var(--text-muted)',
        background: active ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'transparent',
      }}
    >
      <Icon size={20} />
    </button>
  );
};

const MobileToolbar = ({
  selectedNodes,
  selectedNode,
  onMarkNode,
  onToggleBan,
  onDeleteNode,
  onBulkToggleBan,
  onBulkDelete,
}) => {
  if (selectedNodes.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-0.5 px-3 py-1.5 rounded-2xl shadow-lg z-10" style={{ background: 'color-mix(in srgb, var(--bg-deeper) 85%, transparent)', backdropFilter: 'blur(20px)', border: '1px solid var(--border)' }}>
      {selectedNodes.length === 1 && selectedNode ? (
        <>
          <MobileBtn icon={Lightbulb} onClick={() => onMarkNode(selectedNode.id)} active={selectedNode.marked} />
          <MobileBtn icon={Ban} onClick={() => onToggleBan(selectedNode.id)} active={selectedNode.banned} />
          <Divider />
          <MobileBtn icon={Trash2} onClick={() => onDeleteNode(selectedNode)} danger />
        </>
      ) : (
        <>
          <div className="flex items-center px-3 h-12 text-[14px] font-semibold text-slate-600">
            {selectedNodes.length}
          </div>
          <MobileBtn icon={Ban} onClick={onBulkToggleBan} />
          <MobileBtn icon={Trash2} onClick={onBulkDelete} danger />
        </>
      )}
    </div>
  );
};

const ToolBtn = ({ icon, label, onClick, active, danger }) => {
  const Icon = icon;
  return (
    <button
      onClick={onClick}
      title={label}
      className="w-9 h-9 flex items-center justify-center rounded-xl transition-all hover:scale-105 active:scale-95"
      style={{
        color: danger ? 'var(--danger)' : active ? 'var(--accent)' : 'var(--text-muted)',
        background: active ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'transparent',
      }}
      onMouseEnter={e => { if (!danger && !active) e.currentTarget.style.background = 'var(--surface)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = active ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'transparent'; }}
    >
      <Icon size={16} />
    </button>
  );
};

const SingleNodeToolbar = ({
  node,
  onAddNode,
  onEditNode,
  onMarkNode,
  onColorNode,
  onToggleBan,
  onDeleteNode,
}) => {
  const isMarked = node?.marked;
  const isBanned = node?.banned;
  const t = useT();

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5">
      <ToolBtn icon={Plus}      label={t('toolbar.addChild')}  onClick={() => onAddNode(node)} />
      <ToolBtn icon={Pencil}    label={t('toolbar.edit')}      onClick={() => onEditNode(node)} />
      <ToolBtn icon={Lightbulb} label={t('toolbar.mark')}      onClick={() => onMarkNode(node.id)} active={isMarked} />
      <Divider />
      <ToolBtn icon={Palette}   label={t('toolbar.colorBtn')}  onClick={() => onColorNode(node)} />
      <ToolBtn icon={Ban}       label={t('toolbar.ban')}       onClick={() => onToggleBan(node.id)} active={isBanned} />
      <Divider />
      <ToolBtn icon={Trash2}    label={t('toolbar.deleteBtn')} onClick={() => onDeleteNode(node)} danger />
    </div>
  );
};

const BulkToolbar = ({
  count,
  onBulkColorChange,
  onBulkToggleBan,
  onBulkDelete,
}) => {
  const t = useT();
  return (
    <>
      <div className="flex items-center px-4 h-11 rounded-[16px]" style={{ background: 'color-mix(in srgb, var(--accent) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)' }}>
        <span className="text-[14px] font-semibold" style={{ color: 'var(--accent)' }}>
          {t('toolbar.selectedCount', { count })}
        </span>
      </div>
      <button
        onClick={onBulkColorChange}
        className={BTN_TEXT}
        style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)', color: 'var(--accent)' }}
      >
        {t('toolbar.color')}
      </button>
      <button
        onClick={onBulkToggleBan}
        className={BTN_TEXT}
        style={{ background: 'color-mix(in srgb, var(--warning) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)', color: 'var(--warning)' }}
      >
        {t('toolbar.onOff')}
      </button>
      <button
        onClick={onBulkDelete}
        className={BTN_TEXT}
        style={{ background: 'color-mix(in srgb, var(--danger) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)', color: 'var(--danger)' }}
      >
        {t('toolbar.delete')}
      </button>
    </>
  );
};

export const CanvasToolbar = ({
  selectedNodes,
  selectedNode,
  activeParentNode,
  isPanMode,
  onTogglePanMode,
  onAddNode,
  onEditNode,
  onMarkNode,
  onColorNode,
  onToggleBan,
  onDeleteNode,
  onBulkColorChange,
  onBulkToggleBan,
  onBulkDelete,
  onAnalyzeMap,
  hasNodes,
  isAnalyzing,
  reports,
  onOpenReport,
  flashcardMode,
  onToggleFlashcardMode,
  flashcardStats,
  onShareMap,
  isMobile = false,
}) => {
  if (isMobile) {
    return (
      <MobileToolbar
        selectedNodes={selectedNodes}
        selectedNode={selectedNode}
        onMarkNode={onMarkNode}
        onToggleBan={onToggleBan}
        onDeleteNode={onDeleteNode}
        onBulkToggleBan={onBulkToggleBan}
        onBulkDelete={onBulkDelete}
      />
    );
  }

  return (
    <div
      className="fixed bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 px-4 py-3 rounded-2xl shadow-lg z-10"
      style={{
        background: 'color-mix(in srgb, var(--bg-deeper) 85%, transparent)',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--border)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px color-mix(in srgb, var(--accent) 6%, transparent)',
      }}
    >
      {selectedNodes.length > 1 ? (
        <BulkToolbar
          count={selectedNodes.length}
          onBulkColorChange={onBulkColorChange}
          onBulkToggleBan={onBulkToggleBan}
          onBulkDelete={onBulkDelete}
        />
      ) : selectedNodes.length === 1 && selectedNode ? (
        <SingleNodeToolbar
          node={selectedNode}
          onAddNode={onAddNode}
          onEditNode={onEditNode}
          onMarkNode={onMarkNode}
          onColorNode={onColorNode}
          onToggleBan={onToggleBan}
          onDeleteNode={onDeleteNode}
        />
      ) : (
        <ToolbarDefault
          isPanMode={isPanMode}
          onTogglePanMode={onTogglePanMode}
          onAddNode={onAddNode}
          activeParentNode={activeParentNode}
          onAnalyzeMap={onAnalyzeMap}
          hasNodes={hasNodes}
          isAnalyzing={isAnalyzing}
          reports={reports}
          onOpenReport={onOpenReport}
          flashcardMode={flashcardMode}
          onToggleFlashcardMode={onToggleFlashcardMode}
          flashcardStats={flashcardStats}
          onShareMap={onShareMap}
        />
      )}
    </div>
  );
};
